import React, { useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { CheckCircle2Icon, Loader2Icon, UploadIcon } from "lucide-react";
import { useHackathon } from "../../contexts/HackathonContext";
import { schedule } from "../../data/schedule";
import { PublicLayout } from "../../components/layout/PublicLayout";
import { VideoRecorder } from "../../components/VideoRecorder";
import { currentProgramDay, formatBytes, formatDay, PROGRAM_DAYS } from "../../utils/dates";
type Mode = "record" | "upload";
const MAX_BYTES = 1024 * 1024 * 1024;
export function CheckIn() {
  const {
    token
  } = useParams();
  const {
    state,
    addVideo
  } = useHackathon();
  const inputId = useId();
  const student = state.students.find(s => s.inviteToken === token);
  const day = currentProgramDay();
  const [mode, setMode] = useState<Mode>("record");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  if (!student) {
    return <PublicLayout data-id="element-1038" data-mp="w6twz4wx7j71">
        <h1 className="text-2xl font-semibold" data-id="element-1039" data-mp="w6bsxm40o0ok">This check-in link isn't valid</h1>
        <p className="mt-2 text-muted" data-id="element-1040" data-mp="r8a9h02ohzck">Please ask the program team for your personal link.</p>
      </PublicLayout>;
  }
  const firstName = student.name.split(" ")[0];
  if (day < 1 || day > PROGRAM_DAYS || !student.groupId) {
    return <PublicLayout data-id="element-1041" data-mp="1eymeo36jhhzg">
        <h1 className="text-2xl font-semibold" data-id="element-1042" data-mp="1ey4hn4c1yzqh">Hi {firstName}, there's no check-in today</h1>
        <p className="mt-2 text-muted" data-id="element-1043" data-mp="1axknm9ms0c8k">
          {!student.groupId ? "You haven't been assigned to a group yet." : day < 1 ? "Daily videos start on October 7." : "Daily videos ran from October 7 to 12. See you at Demo Day!"}
        </p>
      </PublicLayout>;
  }
  const prompt = schedule.find(s => s.day === day)?.videoPrompt;
  const existing = state.videos.find(v => v.groupId === student.groupId && v.day === day);
  const existingBy = existing?.submittedBy ? state.students.find(s => s.id === existing.submittedBy)?.name : null;
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) return setError("Please choose a video file.");
    if (f.size > MAX_BYTES) return setError("Videos must be under 1 GB.");
    setError(null);
    setFile(f);
  };
  const submit = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      await addVideo({
        groupId: student.groupId,
        day,
        fileName: file.name,
        size: file.size,
        note: note.trim(),
        uploadedAt: new Date().toISOString(),
        submittedBy: student.id
      }, file);
      setDone(true);
    } catch {
      setError("We couldn't save your video. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  if (done) {
    return <PublicLayout data-id="element-1044" data-mp="3hjdpjww5pkty">
        <CheckCircle2Icon className="h-10 w-10 text-brgreen" aria-hidden="true" data-id="element-1045" data-mp="3hikhmslgopa8" />
        <h1 className="mt-4 text-3xl font-semibold tracking-tight" data-id="element-1046" data-mp="3dik6bgtnsu7e">Thanks, {firstName}!</h1>
        <p className="mt-2 text-[15px] text-muted" data-id="element-1047" data-mp="38hmnr34qly5d">
          Group {student.groupId}'s Day {day} video is in. See you tomorrow.
        </p>
        <button type="button" onClick={() => {
        setDone(false);
        setFile(null);
        setNote("");
      }} className="mt-8 rounded-xl border border-ink/15 bg-surface px-5 py-3 text-sm font-semibold transition-colors duration-150 hover:bg-paper" data-id="element-1048" data-mp="33j2x4pf77y6o">
          Replace with a different video
        </button>
      </PublicLayout>;
  }
  return <PublicLayout data-id="element-1049" data-mp="3ktf462kpodjx">
      <p className="text-sm font-semibold text-saffron-ink" data-id="element-1050" data-mp="370fjzn9zhv61">
        Day {day} of {PROGRAM_DAYS} · {formatDay(day, "EEEE, MMM d")} · Group {student.groupId}
      </p>
      <h1 className="mt-1 text-3xl font-semibold tracking-tight" data-id="element-1051" data-mp="3bx64cmstncx9">Hi {firstName}, time for today's video</h1>
      {prompt && <p className="mt-3 text-[17px]" data-id="element-1052" data-mp="7l250ccu5gr7">{prompt}</p>}
      <p className="mt-2 text-sm text-muted" data-id="element-1053" data-mp="7jr0ubfha6zk">Up to 2 minutes · one video per group · due 21:00</p>

      {existing && <p className="mt-6 rounded-xl bg-link-soft px-4 py-3 text-sm text-link" data-id="element-1054" data-mp="3psrnidcdj2hr">
          Your group already submitted today's video{existingBy ? ` (${existingBy}, ` : " ("}
          {format(new Date(existing.uploadedAt), "HH:mm")}). Sending a new one will replace it.
        </p>}

      {!file ? <div className="mt-8" data-id="element-1055" data-mp="2j88kif8yp82e">
          <div role="tablist" aria-label="How to add your video" className="inline-flex rounded-xl bg-ink/5 p-1" data-id="element-1056" data-mp="2bqkff3cko6oe">
            {(["record", "upload"] as Mode[]).map(m => <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors duration-150 ${mode === m ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"}`} data-id="element-1057" data-mp="2krs20o5q7j2n">
                {m === "record" ? "Record now" : "Upload a file"}
              </button>)}
          </div>
          <div className="mt-4" data-id="element-1058" data-mp="1x0moifn9d2vi">
            {mode === "record" ? <VideoRecorder onRecorded={setFile} data-id="element-1059" data-mp="19btmc1g1lysg" /> : <>
                <label htmlFor={inputId} className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-ink/20 bg-surface px-6 text-center transition-colors duration-150 hover:border-ink/50" data-id="element-1060" data-mp="14e1ypjmfkyu2">
                  <UploadIcon className="h-6 w-6" aria-hidden="true" data-id="element-1061" data-mp="3e5bbl1qzogwh" />
                  <span className="mt-3 text-[15px] font-semibold" data-id="element-1062" data-mp="3j4ef2hrbmf2c">Choose a video</span>
                  <span className="mt-1 text-sm text-muted" data-id="element-1063" data-mp="3j6h69547me8n">MP4, MOV or WebM · under 1 GB</span>
                </label>
                <input id={inputId} type="file" accept="video/*" className="sr-only" onChange={pick} data-id="element-1064" data-mp="2qfqrugn0hfm5" />
              </>}
          </div>
        </div> : <div className="mt-8 space-y-5" data-id="element-1065" data-mp="2vhg2hv6bnr9p">
          {preview && <video src={preview} controls playsInline className="aspect-video w-full rounded-2xl bg-navy" data-id="element-1066" data-mp="2zhq3suj65ti8" />}
          <div className="flex items-center justify-between gap-3 text-sm" data-id="element-1067" data-mp="ao2u4j06bj2a">
            <span className="truncate text-muted" data-id="element-1068" data-mp="epoesqiy91n1">
              {file.name} · {formatBytes(file.size)}
            </span>
            <button type="button" onClick={() => setFile(null)} className="shrink-0 font-semibold text-muted hover:text-ink" data-id="element-1069" data-mp="1z771z76xqrxx">
              {mode === "record" ? "Retake" : "Choose another"}
            </button>
          </div>
          <label className="block" data-id="element-1070" data-mp="1qe2a8zvmt777">
            <span className="text-sm font-medium" data-id="element-1071" data-mp="1lhlnoa42z0w8">What did your team accomplish today?</span>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="A sentence or two for the program team" className="mt-1.5 w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-[15px] outline-none focus:border-ink" data-id="element-1072" data-mp="1ghqzwfflqrfe" />
          </label>
          <button type="button" onClick={submit} disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-6 py-3.5 text-[15px] font-semibold text-white transition-[transform,background-color] duration-150 hover:bg-ink/90 active:scale-[0.98] disabled:bg-ink/50" data-id="element-1073" data-mp="12kamul2svzeo">
            {saving && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1074" data-mp="2apg5w1zyy7xf" />}
            {saving ? "Sending video" : `Submit Day ${day} video`}
          </button>
        </div>}

      {error && <p className="mt-3 text-sm text-danger" role="alert" data-id="element-1075" data-mp="20ybwdht2bf9z">
          {error}
        </p>}

      <p className="mt-10 text-sm text-muted" data-id="element-1076" data-mp="1mz3swcpv9kzz">
        Need to update your profile?{" "}
        <Link to={`/join/${student.inviteToken}`} className="font-semibold text-ink underline" data-id="element-1077" data-mp="1r3jw2683iebi">
          Edit it here
        </Link>
      </p>
    </PublicLayout>;
}